import { Localized } from "@fluent/react"
import { useLocation, useNavigate } from "react-router-dom"
import { useAppDispatch, useAppSelector } from "../../../../app/hooks"
import { DocumentCreationMode, DocumentHeader } from "../../documentsApi"
import {
    Alert,
    Box,
    ButtonGroup,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    Switch,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material"
import { showError, showSuccess } from "../../../notifications/notificationsSlice"
import { ChangeEvent, useCallback, useState } from "react"
import { Help } from "../../../dialogs/HelpDialog"
import { If } from "../../../../app/If"
import { AppId, AppTextFieldHandler } from "../../../../app/appTypes"
import ArrowRightIcon from "@mui/icons-material/ArrowRight"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import { createRiskAnalysis, RiskAnalysisHeader } from "../riskAnalysisApi"
import { loadSettingsForCompany } from "../../../systemSettings/systemDocumentSettings/SystemDocumentSettingsApi"
import { StructureTreeSelect } from "../../../structure/StructureTreeSelect"
import { selectContextStructure } from "../../../structure/contextStructureSlice"
import { selectStructureNodeById } from "../../../device/structureSlice"
import { RichTextContainer, tiptapExtensions } from "../../../../app/RichTextEditor"
import { useEditor } from "@tiptap/react"
import { SaveButton } from "../../../../app/components/SaveButton"
import { DocumentNumberTextField } from "../../controls/DocumentNumberTextField"
import { CancelButton } from "../../../../app/components/CancelButton"
import { ClientDocumentTemplateSelectField } from "../../templates/ClientDocumentTemplateSelectField"
import { DocumentSelectField } from "../../controls/DocumentSelectField"

const RequiredError = () => (
    <Localized id="field-is-required">
        <span>_Pole jest wymagane</span>
    </Localized>
)

const DialogSection = ({
    text,
    helpId,
    children,
}: {
    text: React.ReactNode
    helpId?: string
    children: React.ReactNode
}) => (
    <Box
        sx={(theme) => ({
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            overflow: "hidden",
            backgroundColor: theme.palette.background.paper,
        })}
    >
        <Box
            sx={(theme) => ({
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                px: 1.5,
                py: 0.75,
                backgroundColor: theme.palette.grey[100],
                borderBottom: `1px solid ${theme.palette.divider}`,
            })}
        >
            <ArrowRightIcon fontSize="small" />

            <Typography
                variant="subtitle2"
                sx={{
                    fontWeight: 600,
                    color: "text.secondary",
                    letterSpacing: 0.2,
                }}
            >
                {text}
            </Typography>

            {helpId && <Help id={helpId} />}
        </Box>

        <Box
            sx={{
                p: 2,
            }}
        >
            {children}
        </Box>
    </Box>
)

export interface CreateRiskAnalysisDialogProps {
    open?: boolean
    onClose?: () => void
}

type RiskAnalysisCreationOption = "ISHIKAWA" | "FMEA" | "FROM_URS"
type TemplateMode = "Empty" | "Template"

export const CreateRiskAnalysisDialog = ({ open = true, onClose }: CreateRiskAnalysisDialogProps) => {
    const navigate = useNavigate()
    const dispatch = useAppDispatch()
    const { state, search } = useLocation()
    const location = useLocation()
    const defaultUrsId = state && state[`defaultUrsId`] ? String(state[`defaultUrsId`]) : null

    const isSupplierRiskAnalysis = location.pathname.includes("supplier-risk-analysis")
    const params = new URLSearchParams(search)

    const [documentNumber, setDocumentNumber] = useState("")
    const [title, setTitle] = useState("")
    const [language, setLanguage] = useState("PL")
    const [selectedOption, setSelectedOption] = useState<RiskAnalysisCreationOption>(defaultUrsId ? "FROM_URS" : "ISHIKAWA")
    const [templateMode, setTemplateMode] = useState<TemplateMode>("Empty")
    const [templateId, setTemplateId] = useState("")
    const [isTestsEnabled, setIsTestsEnabled] = useState(true)
    const { data: contextStructure } = useAppSelector(selectContextStructure)
    const contextStructureId = contextStructure && contextStructure.mode === "manual" ? contextStructure.structId : ""
    const structure = useAppSelector((state) => selectStructureNodeById(state, contextStructureId))
    const [deviceTypeId, setDeviceTypeId] = useState<AppId | undefined>(
        structure && structure.subStructureIds && structure.subStructureIds.length === 0 ? structure.id : ""
    )

    const editor = useEditor({
        extensions: tiptapExtensions,
        content: "",
    })

    const [selectedUrsId, setSelectedUrsId] = useState<string | null>(defaultUrsId)
    const [selectedUrs, setSelectedUrs] = useState<DocumentHeader | null>(null)

    const [saving, setSaving] = useState(false)
    const [validationErrors, setValidationErrors] = useState(false)
    const [serverError, setServerError] = useState("")
    const isFromUrs = selectedOption === "FROM_URS"
    const useTemplate = templateMode === "Template"
    const creationMode: DocumentCreationMode = useTemplate ? "Template" : isFromUrs ? "Document" : "Empty"

    const titleError = validationErrors && title === ""
    const ursError = validationErrors && isFromUrs && !selectedUrsId
    const templateRequiredError = validationErrors && useTemplate && templateId === ""

    const handleDeviceTypeIdChange = useCallback((structureId) => {
        setDeviceTypeId(structureId)
        setTemplateId("")
    }, [])

    const handleTitleChange: AppTextFieldHandler = useCallback((e) => {
        setTitle(e.target.value)
    }, [])

    const handleDocumentNumberChange: (v: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void =
        useCallback((e) => {
            setDocumentNumber(e.target.value)
        }, [])

    const handleCloseButtonClick = useCallback(() => {
        if (onClose) {
            onClose()
        }
    }, [onClose])

    const handleDialogClose = useCallback((_: object, reason: "backdropClick" | "escapeKeyDown") => {
        if (reason === "backdropClick") return
        handleCloseButtonClick()
    }, [handleCloseButtonClick])

    const handleSaveButtonClick = async () => {
        setValidationErrors(true)
        setServerError("")

        if (!documentNumber || !title) return
        if (isFromUrs && !selectedUrsId) return
        if (useTemplate && templateId === "") return

        setSaving(true)
        const docDescription = editor?.getHTML() ?? ""
        try {
            const resultSettings = await dispatch(loadSettingsForCompany(`?documentCode=RiskAnalysis`))
            if (loadSettingsForCompany.fulfilled.match(resultSettings)) {
                const goodThreshold = resultSettings.payload.find((f) => f.name === "GOOD_THRESHOLD")
                const warningThreshold = resultSettings.payload.find((f) => f.name === "WARNING_THRESHOLD")

                const result = await dispatch(
                    createRiskAnalysis({
                        documentType: "RA",
                        creationMode,
                        structureId: deviceTypeId,
                        documentNumber: documentNumber,
                        templateId,
                        documentId: selectedUrsId || "",
                        title: title,
                        language: language,
                        settings: {
                               numberingMode: "SIMPLE",
                            goodThreshold: goodThreshold !== undefined ? goodThreshold.value : "0",
                            warningThreshold: warningThreshold !== undefined ? warningThreshold.value : "0",
                            testsEnabled: isTestsEnabled ? "TRUE" : "FALSE",
                        },
                        description: docDescription,
                        purpose: "",
                    })
                )

                if (createRiskAnalysis.fulfilled.match(result)) {
                    const newDocument = result.payload as RiskAnalysisHeader
                    dispatch(showSuccess("saved"))
                    navigate(
                        isSupplierRiskAnalysis
                            ? `/supplier-risk-analysis/edit/${newDocument.id}`
                            : `/new-risk-analysis/edit/${newDocument.id}`,
                        { replace: true }
                    )
                } else {
                    let errorMsg = "error"
                    if (result.payload?.kind === "http") {
                        errorMsg = result.payload.problem?.title ?? errorMsg
                    }
                    setServerError(errorMsg)
                    dispatch(showError(errorMsg))
                }
            } else {
                let errorMsg = "error"
                if ((resultSettings as any).payload?.kind === "http") {
                    errorMsg = (resultSettings as any).payload.problem?.title ?? errorMsg
                }
                setServerError(errorMsg)
                dispatch(showError(errorMsg))
            }
        } catch {
            setServerError("error")
            dispatch(showError("error"))
        } finally {
            setSaving(false)
        }
    }

    return (
        <>
            <Dialog open={open} fullWidth maxWidth="md" onClose={handleDialogClose}>
                <DialogTitle>
                    <Localized id="risk-analysis-create-title">
                        <span>Nowa analiza ryzyka</span>
                    </Localized>
                </DialogTitle>

                <DialogContent dividers>
                    <Grid container spacing={2.25}>
                        <Grid item xs={12}>
                            <DialogSection
                                text={
                                    <Localized id="risk-analysis-select-type">
                                        <span>_Jaką analizę ryzyka wygenerować?</span>
                                    </Localized>
                                }
                                helpId="8be28b75-4ca8-4967-8cdb-91a3a1828c2e"
                            >
                                <ToggleButtonGroup
                                    value={selectedOption}
                                    exclusive
                                    size="small"
                                    sx={{
                                        alignSelf: "flex-start",
                                        "& .MuiToggleButton-root.Mui-selected": {
                                            borderColor: "success.main",
                                            borderWidth: 1.5,
                                        },
                                    }}
                                    onChange={(_, v) => {
                                        if (!v) return
                                        setSelectedOption(v)
                                        setValidationErrors(false)
                                        if (v !== "FROM_URS") {
                                            setSelectedUrsId(null)
                                            setSelectedUrs(null)
                                        }
                                    }}
                                    fullWidth
                                >
                                    <ToggleButton value="ISHIKAWA">
                                        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
                                            {selectedOption === "ISHIKAWA" && <CheckCircleIcon fontSize="small" color="success" />}
                                            <Typography variant="body2">Ishikawa</Typography>
                                        </Box>
                                    </ToggleButton>
                                    <ToggleButton value="FMEA">
                                        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
                                            {selectedOption === "FMEA" && <CheckCircleIcon fontSize="small" color="success" />}
                                            <Typography variant="body2">FMEA</Typography>
                                        </Box>
                                    </ToggleButton>
                                    <ToggleButton value="FROM_URS">
                                        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
                                            {selectedOption === "FROM_URS" && <CheckCircleIcon fontSize="small" color="success" />}
                                            <Typography variant="body2">From URS</Typography>
                                        </Box>
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </DialogSection>
                        </Grid>

                        <If condition={isFromUrs}>
                            <Grid item xs={12}>
                                <DialogSection
                                    text={
                                        <Localized id="urs-create-based-on-document-select">
                                            <span>_Wybierz URS na podstawie którego zostanie wygenerowany dokument</span>
                                        </Localized>
                                    }
                                >
                                    <DocumentSelectField
                                        documentType="URS"
                                        value={selectedUrsId}
                                        selected={selectedUrs}
                                        filterStructureId={deviceTypeId ? String(deviceTypeId) : null}
                                        reserveHelperSpace={false}
                                        onChange={(id, doc) => {
                                            setSelectedUrsId(id)
                                            setSelectedUrs(doc ?? null)
                                        }}
                                    />
                                    {ursError && <FormHelperText error><RequiredError /></FormHelperText>}
                                </DialogSection>
                            </Grid>
                        </If>

                        <Grid item xs={12}>
                            <DialogSection text={<Localized id="base-data"><span>_Dane podstawowe</span></Localized>}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <StructureTreeSelect
                                            defaultStructureId={deviceTypeId}
                                            onStructureSelected={handleDeviceTypeIdChange}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={8}>
                                        <DocumentNumberTextField
                                            fullWidth={true}
                                            required={true}
                                            value={documentNumber}
                                            onChange={handleDocumentNumberChange}
                                            label={
                                                <Localized id="document-number">
                                                    <span>_Numer dokumentu</span>
                                                </Localized>
                                            }
                                            documentType={"RA"}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={4}>
                                        <FormControl fullWidth>
                                            <InputLabel id="app-language-label">
                                                <Localized id="document-language">Język</Localized>
                                            </InputLabel>
                                            <Select
                                                labelId="app-language-label"
                                                id="app-language"
                                                value={language}
                                                onChange={(e) => setLanguage(e.target.value)}
                                                label={<Localized id="document-language">Język</Localized>}
                                            >
                                                <MenuItem value={"PL"}>PL</MenuItem>
                                                <MenuItem value={"EN"}>EN</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12} sx={{ mt: -0.5 }}>
                                        <TextField
                                            helperText={titleError && <RequiredError />}
                                            required
                                            fullWidth
                                            value={title}
                                            error={titleError}
                                            onChange={handleTitleChange}
                                            label={
                                                <Localized id="urs-title">
                                                    <span>_Tytuł</span>
                                                </Localized>
                                            }
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={isTestsEnabled}
                                                    onChange={(event) => setIsTestsEnabled(event.target.checked)}
                                                    color="primary"
                                                />
                                            }
                                            label={<Localized id="risk-analysis-tests-enabled-switch"><span>Testy</span></Localized>}
                                        />
                                    </Grid>
                                </Grid>
                            </DialogSection>
                        </Grid>

                        <Grid item xs={12}>
                            <DialogSection
                                text={
                                    <Localized id="document-structure-template">
                                        <span>_Document structure template</span>
                                    </Localized>
                                }
                            >
                                <RadioGroup
                                    row
                                    value={templateMode}
                                    onChange={(e) => {
                                        const mode = e.target.value as TemplateMode
                                        setTemplateMode(mode)
                                        if (mode === "Empty") {
                                            setTemplateId("")
                                        }
                                    }}
                                >
                                    <FormControlLabel
                                        value="Empty"
                                        control={<Radio size="small" />}
                                        sx={{ mr: 2 }}
                                        label={
                                            <Typography variant="body2">
                                                <Localized id="document-creation-mode-empty">
                                                    <span>_Pusty</span>
                                                </Localized>
                                            </Typography>
                                        }
                                    />
                                    <FormControlLabel
                                        value="Template"
                                        control={<Radio size="small" />}
                                        label={
                                            <Typography variant="body2">
                                                <Localized id="document-creation-mode-template">
                                                    <span>_template</span>
                                                </Localized>
                                            </Typography>
                                        }
                                    />
                                </RadioGroup>

                                {templateMode === "Template" && (
                                    <Box sx={{ mt: 2 }}>
                                        {deviceTypeId ? (
                                            <FormControl fullWidth error={templateRequiredError}>
                                                <ClientDocumentTemplateSelectField
                                                    structureId={Number(deviceTypeId)}
                                                    documentType="RA"
                                                    value={templateId}
                                                    language={language}
                                                    filterStructureId={Number(deviceTypeId)}
                                                    showFiltersTab={true}
                                                    onChange={(id) => setTemplateId(id)}
                                                />
                                                {templateRequiredError && (
                                                    <FormHelperText>
                                                        <Localized id="document-template-select">
                                                            <span>_Wybierz szablon</span>
                                                        </Localized>
                                                    </FormHelperText>
                                                )}
                                            </FormControl>
                                        ) : (
                                            <FormControl fullWidth error>
                                                <TextField
                                                    fullWidth
                                                    disabled
                                                    label={
                                                        <Localized id="menu-document-templates">
                                                            <span>_template</span>
                                                        </Localized>
                                                    }
                                                    value=""
                                                />
                                                <FormHelperText>
                                                    <Localized id="document-structure-needed-for-template-warning">
                                                        <span>_structure needed.</span>
                                                    </Localized>
                                                </FormHelperText>
                                            </FormControl>
                                        )}
                                    </Box>
                                )}
                            </DialogSection>
                        </Grid>

                        <If condition={!isFromUrs}>
                            <Grid item xs={12}>
                                <DialogSection text={<Localized id="situation-description"><span>_Opis sytuacji</span></Localized>}>
                                    <RichTextContainer label={<Localized id="description"><span>_Opis</span></Localized>} editor={editor} />
                                </DialogSection>
                            </Grid>
                        </If>
                    </Grid>

                    {serverError && (
                        <Alert
                            sx={{ mt: 2, position: "sticky", bottom: 0, m: 0, borderRadius: 0 }}
                            severity="error"
                            onClose={() => setServerError("")}
                        >
                            <Localized id={serverError}>
                                <span>_Server error</span>
                            </Localized>
                        </Alert>
                    )}
                </DialogContent>

                <DialogActions>
                    <ButtonGroup variant="contained" color="secondary">
                        <SaveButton loading={saving} onClick={handleSaveButtonClick} />
                        <CancelButton loading={saving} onClick={handleCloseButtonClick} />
                    </ButtonGroup>
                </DialogActions>
            </Dialog>
        </>
    )
}
